import React, { useEffect, useRef, useCallback } from 'react';
import { ComponentProps } from '@greystar/ui';
import { Placeholder } from '@sitecore-jss/sitecore-jss-nextjs';
import { PageSection } from '@greystar/designsystem';
import { useAnalytics } from '@greystar/lib';

const SeoFooter = (props: ComponentProps): JSX.Element => {
  const footerRef = useRef<HTMLDivElement>(null);
  const analytics = useAnalytics();

  const handleLinkClick = useCallback(
    (event: MouseEvent) => {
      const target = event.currentTarget as HTMLAnchorElement;
      if (!target || target.tagName !== 'A') return;

      try {
        const frame = target.closest('.footer-seo-frame');
        const titleElement = frame?.querySelector('.footer-seo-frame--title');
        const title = titleElement?.textContent?.trim() ?? '';

        const isExternal =
          target.href.startsWith('mailto:') ||
          (target.href.startsWith('http') && !target.href.includes(window.location.hostname));

        const eventData = {
          event: 'footer_interaction',
          action_type: 'link click - seo footer',
          module_name: 'seo footer',
          content_group: title,
          inpage_location: 'bottom',
          click_type: isExternal ? 'external' : 'internal',
          click_text: target.textContent?.trim() ?? '',
          destination_url: target.href,
        };

        analytics.trackEvent('footer_interaction', eventData);

        // Safeguard for SSR/SSG where window might not be available
        if (typeof window !== 'undefined') {
          window.location.href = target.href;
        }
      } catch (error) {
        console.error('Error handling footer link click:', error);
      }
    },
    [analytics]
  );

  useEffect(() => {
    const footerElement = footerRef.current;
    if (!footerElement) return;
    const footerLinks = footerElement.querySelectorAll('a');

    footerLinks.forEach((link) => {
      link.addEventListener('click', handleLinkClick);
    });

    return () => {
      footerLinks.forEach((link) => {
        link.removeEventListener('click', handleLinkClick);
      });
    };
  }, [handleLinkClick]);

  return (
    <PageSection as="article">
      <div className="footer-seo" ref={footerRef}>
        <Placeholder
          name={`seo-footer-${props.params.DynamicPlaceholderId}`}
          rendering={props.rendering}
        />
      </div>
    </PageSection>
  );
};

export default SeoFooter;
